import React, { useEffect, useMemo, useRef, useState } from "react";
import { connect, ConnectedProps } from "react-redux";
import { Plus, Wand2, Trash2, RefreshCw, LayoutGrid, AlignLeft } from "lucide-react";
import { RootState } from "@/store";
import { GeneralPlayer } from "@/components/CleoPlayer";
import type { GeneralPlayerHandle } from "@/components/CleoPlayer";
import { secondsToHms } from "../../../utils";
import { hmsToSeconds } from "@/components/CleoPlayer/utils";
import SectionHeader from "@/components/Layout/SectionHeader";

interface ChapterItem {
  id: number;
  title: string;
  start: number;
}

const CHAPTER_COLORS = ["#3b5bdb", "#12805c", "#b8860b", "#a3195b"];

let nextChapterId = 1;

const MediaChapters = ({ currentMediaDetailsForEdit }: Props) => {
  const generalPlayerRef = useRef<GeneralPlayerHandle>(null);
  const duration = currentMediaDetailsForEdit?.meta?.duration || 0;

  const [currentPlayerTime, setCurrentPlayerTime] = useState(0);
  const [chapters, setChapters] = useState<ChapterItem[]>([
    { id: nextChapterId++, title: "Chapter 1", start: 0 },
  ]);
  const [activeChapterId, setActiveChapterId] = useState<number | null>(
    chapters[0]?.id ?? null
  );
  const [hoveredChapterId, setHoveredChapterId] = useState<number | null>(
    null
  );
  const [viewMode, setViewMode] = useState<"card" | "text">("text");
  const [chapterText, setChapterText] = useState(
    "0:00:00 Chapter 1"
  );
  const [chapterTextError, setChapterTextError] = useState<string | null>(
    null
  );
  const [isPaused, setIsPaused] = useState(true);
  const [startInput, setStartInput] = useState("00:00:00");
  const isTextFocused = useRef(false);
  const trackRef = useRef<HTMLDivElement>(null);
  const [hoverTime, setHoverTime] = useState<number | null>(null);
  const [isAddButtonHovered, setIsAddButtonHovered] = useState(false);

  const videos = [
    {
      id: currentMediaDetailsForEdit?.id || "1",
      title: currentMediaDetailsForEdit?.title || "Current Video",
      duration,
    },
  ];

  const orderedChapters = useMemo(
    () => [...chapters].sort((a, b) => a.start - b.start),
    [chapters]
  );

  const segments = useMemo(
    () =>
      orderedChapters.map((chapter, index) => {
        const end =
          index < orderedChapters.length - 1
            ? orderedChapters[index + 1].start
            : duration;
        return { ...chapter, end, durationSec: Math.max(0, end - chapter.start) };
      }),
    [orderedChapters, duration]
  );

  const activeSegment =
    segments.find((s) => s.id === activeChapterId) || segments[0];

  useEffect(() => {
    if (activeSegment) {
      setStartInput(secondsToHms(activeSegment.start));
    }
  }, [activeSegment?.id, activeSegment?.start]);

  const clamp = (value: number, min: number, max: number) =>
    Math.min(Math.max(value, min), max);

  const handleSkip = (offset: number) => {
    const time = clamp(currentPlayerTime + offset, 0, duration || 0);
    generalPlayerRef.current?.seek(time);
    setCurrentPlayerTime(time);
  };

  const handlePlayPause = () => {
    if (!generalPlayerRef.current) return;
    if (generalPlayerRef.current.isPaused()) {
      generalPlayerRef.current.play();
      setIsPaused(false);
    } else {
      generalPlayerRef.current.pause();
      setIsPaused(true);
    }
  };

  const timeFromClientX = (clientX: number) => {
    if (!trackRef.current || !duration) return 0;
    const { left, width } = trackRef.current.getBoundingClientRect();
    const ratio = clamp((clientX - left) / width, 0, 1);
    return ratio * duration;
  };

  const handleTrackMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    const time = timeFromClientX(e.clientX);
    setHoverTime(time);
    const hovered = segments.find(
      (s) => time >= s.start && time < s.end
    );
    setHoveredChapterId(hovered ? hovered.id : null);
  };

  const handleTrackMouseLeave = () => {
    setHoverTime(null);
    setHoveredChapterId(null);
  };

  const handleTrackClick = (e: React.MouseEvent<HTMLDivElement>) => {
    const time = timeFromClientX(e.clientX);
    generalPlayerRef.current?.seek(time);
    setCurrentPlayerTime(time);
  };

  const commitStartInput = () => {
    if (!activeSegment) return;
    const parsed = hmsToSeconds(startInput);
    if (Number.isNaN(parsed)) {
      setStartInput(secondsToHms(activeSegment.start));
      return;
    }
    const newStart = clamp(parsed, 0, (duration || 0) - 1);
    setChapters((prev) =>
      prev.map((c) =>
        c.id === activeSegment.id ? { ...c, start: newStart } : c
      )
    );
    setStartInput(secondsToHms(newStart));
  };

  const handleAddAtPlayhead = () => {
    const time = Math.floor(currentPlayerTime);
    if (chapters.some((c) => c.start === time)) return;
    const id = nextChapterId++;
    setChapters((prev) => [
      ...prev,
      { id, title: `Chapter ${prev.length + 1}`, start: time },
    ]);
    setActiveChapterId(id);
  };

  const updateChapterTitle = (id: number, title: string) => {
    setChapters((prev) =>
      prev.map((c) => (c.id === id ? { ...c, title } : c))
    );
  };

  const deleteChapter = (id: number) => {
    setChapters((prev) => prev.filter((c) => c.id !== id));
  };

  const handleAddChapterCard = () => {
    const lastSegment = segments[segments.length - 1];
    const time = lastSegment
      ? Math.min(Math.floor(lastSegment.end), Math.floor(duration || 0))
      : 0;
    if (chapters.some((c) => c.start === time)) return;
    const id = nextChapterId++;
    setChapters((prev) => [
      ...prev,
      { id, title: `Chapter ${prev.length + 1}`, start: time },
    ]);
    setActiveChapterId(id);
  };

  const formatChaptersAsText = (list: typeof segments) =>
    list.map((s) => `${secondsToHms(s.start)} ${s.title}`).join("\n");

  const parseChapterText = (text: string) =>
    text
      .split("\n")
      .map((line) => line.trim())
      .filter(Boolean)
      .map((line) => {
        const match = line.match(/^(\d{1,2}(?::\d{2}){1,2})\s+(.+)$/);
        if (!match) return null;
        const seconds = hmsToSeconds(match[1]);
        if (Number.isNaN(seconds)) return null;
        return { title: match[2].trim(), start: seconds };
      })
      .filter((c): c is { title: string; start: number } => c !== null);

  const findExcessChapterLines = (text: string) => {
    if (!duration) return [];
    return text
      .split("\n")
      .map((line, index) => ({ line: line.trim(), index }))
      .filter(({ line }) => Boolean(line))
      .filter(({ line }) => {
        const match = line.match(/^(\d{1,2}(?::\d{2}){1,2})\s+(.+)$/);
        if (!match) return false;
        const seconds = hmsToSeconds(match[1]);
        return !Number.isNaN(seconds) && seconds >= duration;
      })
      .map(({ index }) => index + 1);
  };

  useEffect(() => {
    if (isTextFocused.current) return;
    setChapterText(formatChaptersAsText(segments));
  }, [segments]);

  const switchToTextView = () => {
    setChapterText(formatChaptersAsText(segments));
    setViewMode("text");
  };

  const applyChapterText = () => {
    const parsed = parseChapterText(chapterText);
    if (parsed.length === 0) {
      setChapterTextError("No valid chapters found.");
      return;
    }
    const excessLines = findExcessChapterLines(chapterText);
    if (excessLines.length > 0) {
      setChapterTextError(
        `Chapter start time cannot exceed video length (${secondsToHms(duration)}).`
      );
      return;
    }
    setChapterTextError(null);
    setChapters(parsed.map((c) => ({ id: nextChapterId++, ...c })));
  };

  const handleChapterTextBlur = () => {
    isTextFocused.current = false;
  };

  if (!currentMediaDetailsForEdit?.id) {
    return null;
  }

  return (
    <div className="w-100 p-3">
      <SectionHeader
        title="Chapters"
        subtitle="Complete lifecycle of this video · Newest first"
        buttonText="Sync from YouTube"
        buttonIcon={<RefreshCw size={14} />}
        // onButtonClick={() => fetchLogs()}
      />
      <div className="row g-4">
        <div className="col col-5">
          <div className="d-flex align-items-center">
          <div className="d-flex flex-grow-1 ">
            <button
              type="button"
              title="Textarea"
              className={`btn btn-sm d-flex align-items-center gap-2 ${
                viewMode === "text" ? "btn-primary" : "btn-outline-secondary"
              }`}
              onClick={switchToTextView}>
              <AlignLeft size={14} />
            </button>
            <button
              type="button"
              title="Card view"
              className={`btn btn-sm d-flex align-items-center gap-2 ${
                viewMode === "card" ? "btn-primary" : "btn-outline-secondary"
              }`}
              onClick={() => setViewMode("card")}>
              <LayoutGrid size={14} />
            </button>
          </div>

          <div className="f-14">
            <span className="text-muted" style={{ fontSize: "13px" }}>
                {segments.length} chapters
              </span>
          </div>
          </div>
          {viewMode === "text" ? (
            <div className="d-flex flex-column gap-2 bg-light p-2 rounded">
              <textarea
                className={`form-control ${chapterTextError ? "is-invalid" : ""}`}
                rows={10}
                style={{
                  fontFamily: "monospace",
                  fontSize: "13px",
                  borderColor: chapterTextError ? "#dc3545" : undefined,
                }}
                placeholder={"0:00 Introduction\n1:24 Getting started"}
                value={chapterText}
                onFocus={() => {
                  isTextFocused.current = true;
                }}
                onChange={(e) => {
                  setChapterText(e.target.value);
                  setChapterTextError(null);
                }}
                onBlur={handleChapterTextBlur}
              />

              {chapterTextError && (
                <div className="text-danger" style={{ fontSize: "12px" }}>
                  {chapterTextError}
                </div>
              )}
              <div className="d-flex align-items-center justify-content-between">
                <div className="text-muted" style={{ fontSize: "12px" }}>
                  One chapter per line: <code>hh:mm:ss Title</code>, same as
                  YouTube description chapters.
                </div>
                <button
                  type="button"
                  className="btn btn-sm btn-primary flex-shrink-0"
                  onClick={applyChapterText}
                >
                  Apply
                </button>
              </div>
            </div>
          ) : (
          <div className="d-flex flex-column gap-3 mt-2">
            {segments.map((segment, index) => (
              <div
                key={segment.id}
                className="border rounded p-2"
                style={{
                  borderColor:
                    activeChapterId === segment.id
                      ? CHAPTER_COLORS[index % CHAPTER_COLORS.length]
                      : "#e9ecef",
                  borderWidth: activeChapterId === segment.id ? 2 : 1,
                }}
                onClick={() => setActiveChapterId(segment.id)}>
                <div className="d-flex align-items-start gap-3">
                  <div
                    className="d-flex align-items-center justify-content-center flex-shrink-0"
                    style={{
                      width: 28,
                      height: 28,
                      borderRadius: 6,
                      background: '#eee',
                      color: "#666",
                      fontWeight: 600,
                    }}>
                    {index + 1}
                  </div>
                  <div className="flex-grow-1">
                    <input
                      type="text"
                      className="form-control bg-light border-0 mb-2"
                      value={segment.title}
                      onChange={(e) =>
                        updateChapterTitle(segment.id, e.target.value)
                      }
                    />
                    <div className="d-flex align-items-center gap-2 text-muted">
                      <span className="badge bg-light text-dark border" style={{fontSize: '13px'}}>
                        {secondsToHms(segment.start)}
                      </span>
                      {index === 0 ? (
                        <span className="badge bg-secondary" style={{fontSize: '10px', fontWeight: 'normal'}}>START</span>
                      ) : null}
                      <span className="text-muted">→</span>
                      <span className="text-muted" style={{fontSize: '13px'}}>
                        {secondsToHms(segment.end)} · {Math.round(segment.durationSec)}s
                      </span>
                    </div>
                  </div>
                  <div className="d-flex flex-column gap-2 flex-shrink-0">
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-danger"
                      title="Delete chapter"
                      disabled={index === 0}
                      onClick={(e) => {
                        e.stopPropagation();
                        deleteChapter(segment.id);
                      }}
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
              </div>
            ))}
            <div className="d-flex justify-content-end">
              <button
                type="button"
                className="btn btn-sm btn-primary d-flex align-items-center gap-2"
                onClick={handleAddChapterCard}
              >
                <Plus size={14} />
                Add Chapter
              </button>
            </div>
          </div>
          )}
        </div>
        <div className="col col-7">
          <div style={{ borderRadius: 10, overflow: "hidden" }}>
            <GeneralPlayer
              ref={generalPlayerRef}
              id={currentMediaDetailsForEdit?.id}
              onTimeUpdate={setCurrentPlayerTime}
              videos={videos}
              autoPlay={3}
            />
          </div>

          <div className="mt-3 position-relative" style={{ zIndex: 10 }}>
            <div className="d-flex justify-content-between position-relative" style={{ height: "18px" }}>
              {Array.from({ length: 4 }, (_, i) => {
                const time = Math.round(((duration || 0) / 3) * i);
                return (
                  <div key={i} className="d-flex flex-column align-items-start" style={{ position: "relative" }}>
                    <span style={{ width: 1, height: 8, background: "#adb5bd" }} />
                    <span className="text-muted" style={{ fontSize: "12px", marginTop: 2 }}>
                      {Math.round(time)}s
                    </span>
                  </div>
                );
              })}
            </div>

            <div className="d-flex align-items-center" style={{ gap: "10px" }}>
              <div
                ref={trackRef}
                className="d-flex position-relative flex-grow-1"
                onClick={handleTrackClick}
                onMouseMove={handleTrackMouseMove}
                onMouseLeave={handleTrackMouseLeave}
                style={{
                  height: "30px",
                  borderRadius: "6px",
                  overflow: "visible",
                  background: "#e7f0ff",
                  cursor: "pointer",
                }}
              >
                {segments.map((segment, index) => (
                  <div
                    key={segment.id}
                    style={{
                      position: "relative",
                      flex: `${Math.max(segment.durationSec, 1)} 0 auto`,
                      background: '#8ab2f3',
                      pointerEvents: "none",
                      borderRight: "2px solid #fff",
                      borderRadius:
                        index === 0
                          ? "6px 0 0 6px"
                          : index === segments.length - 1
                          ? "0 6px 6px 0"
                          : undefined,
                    }}
                  />
                ))}

                {hoverTime !== null && duration > 0 && !isAddButtonHovered && (() => {
                  const hovered = segments.find((s) => s.id === hoveredChapterId);
                  const hoverRatio = clamp(hoverTime, 0, duration) / duration;
                  const BADGE_HALF_WIDTH = 90;
                  const badgeLeft = `clamp(${BADGE_HALF_WIDTH}px, calc(${
                    hoverRatio * 100
                  }%), calc(100% - ${BADGE_HALF_WIDTH}px))`;
                  return (
                    <>
                      <div
                        style={{
                          position: "absolute",
                          top: "-2px",
                          bottom: "-3px",
                          left: `${hoverRatio * 100}%`,
                          width: "2px",
                          marginLeft: "-1px",
                          background: "rgba(255,255,255,0.6)",
                          zIndex: 14,
                          pointerEvents: "none",
                        }}
                      />
                      <div
                        style={{
                          position: "absolute",
                          top: "100%",
                          left: badgeLeft,
                          transform: "translateX(-50%)",
                          marginTop: "6px",
                          maxWidth: `${BADGE_HALF_WIDTH * 2}px`,
                          background: "rgba(0,0,0,0.4)",
                          color: "#fff",
                          padding: "4px 8px",
                          borderRadius: "4px",
                          fontSize: "11px",
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          zIndex: 20,
                          pointerEvents: "none",
                        }}
                      >
                        {secondsToHms(hoverTime)}
                        {hovered ? ` · ${hovered.title}` : ""}
                      </div>
                    </>
                  );
                })()}

                {(() => {
                  const playheadRatio =
                    (duration || 0) > 0
                      ? clamp(currentPlayerTime, 0, duration) / duration
                      : 0;
                  return (
                    <div
                      style={{
                        position: "absolute",
                        top: "-4px",
                        bottom: "-4px",
                        left: `${playheadRatio * 100}%`,
                        width: "3px",
                        marginLeft: "-1.5px",
                        background: "#fff",
                        borderRadius: "2px",
                        boxShadow: "0 0 0 1px rgba(0,0,0,0.6), 0 0 4px rgba(0,0,0,0.4)",
                        zIndex: 12,
                        pointerEvents: "none",
                      }}
                    />
                  );
                })()}

                {isPaused && (() => {
                  const playheadRatio =
                    (duration || 0) > 0
                      ? clamp(currentPlayerTime, 0, duration) / duration
                      : 0;
                  const BUTTON_HALF_WIDTH = 45;
                  const buttonLeft = `clamp(${BUTTON_HALF_WIDTH}px, calc(${
                    playheadRatio * 100
                  }% ), calc(100% - ${BUTTON_HALF_WIDTH}px))`;
                  return (
                    <button
                      type="button"
                      className="btn btn-sm border-0 btn-primary d-flex align-items-center gap-1 text-nowrap"
                      style={{
                        position: "absolute",
                        top: "-30px",
                        left: buttonLeft,
                        transform: "translateX(-50%)",
                        zIndex: 13,
                        pointerEvents: "auto",
                      }}
                      onMouseEnter={() => setIsAddButtonHovered(true)}
                      onMouseLeave={() => setIsAddButtonHovered(false)}
                      onClick={handleAddAtPlayhead}
                    >
                      <Plus size={10} /> Chapter
                    </button>
                  );
                })()}
              </div>
            </div>
          </div>

          <div
            className="d-flex align-items-center justify-content-center mt-3"
            style={{ gap: "16px" }}
          >
            <div className="d-flex flex-column" style={{ gap: "4px" }}>
              <sub
                style={{
                  fontSize: "10px",
                  fontWeight: 600,
                  letterSpacing: "0.04em",
                  color: "#999",
                }}
              >
                START
              </sub>
              <input
                type="text"
                value={startInput}
                onChange={(e) => setStartInput(e.target.value)}
                onBlur={commitStartInput}
                onKeyDown={(e) => {
                  if (e.key === "Enter") commitStartInput();
                }}
                style={{
                  width: "76px",
                  fontSize: "11px",
                  color: "#212529",
                  background: "#f1f2f4",
                  border: "none",
                  borderRadius: "6px",
                  padding: "6px 10px",
                  fontVariantNumeric: "tabular-nums",
                }}
              />
            </div>

            <div
              className="d-flex align-items-center"
              style={{ gap: "6px", background: "#f1f2f4", borderRadius: "999px" }}
            >
              {renderRoundButton(
                "Back 20s",
                <i className="bi bi-skip-start-fill f-16" />,
                () => handleSkip(-20)
              )}
              {renderRoundButton(
                "Back 10s",
                <i className="bi bi-caret-left-fill f-16" />,
                () => handleSkip(-10)
              )}
              {renderRoundButton(
                isPaused ? "Play" : "Pause",
                <i
                  className={isPaused ? "bi bi-play-fill f-16" : "bi bi-pause-fill f-16"}
                  style={{ fontSize: "18px", color: "#fff" }}
                />,
                handlePlayPause,
                true
              )}
              {renderRoundButton(
                "Forward 10s",
                <i className="bi bi-caret-right-fill f-16" />,
                () => handleSkip(10)
              )}
              {renderRoundButton(
                "Forward 20s",
                <i className="bi bi-skip-end-fill f-16" />,
                () => handleSkip(20)
              )}
            </div>

            <div className="d-flex flex-column" style={{ gap: "4px" }}>
              <sub
                style={{
                  fontSize: "10px",
                  fontWeight: 600,
                  letterSpacing: "0.04em",
                  color: "#999",
                }}
              >
                END
              </sub>
              <input
                type="text"
                value={activeSegment ? secondsToHms(activeSegment.end) : "00:00:00"}
                disabled
                style={{
                  width: "76px",
                  fontSize: "11px",
                  color: "#212529",
                  background: "#f1f2f4",
                  border: "none",
                  borderRadius: "6px",
                  padding: "6px 10px",
                  fontVariantNumeric: "tabular-nums",
                }}
              />
            </div>
          </div>

          <div className="text-muted mt-4 text-center" style={{ fontSize: "11px" }}>
            This is exactly what the viewer sees — segmented scrub bar, hover
            titles, chapter jump.
          </div>
        </div>
      </div>
    </div>
  );
};

const renderRoundButton = (
  title: string,
  icon: React.ReactNode,
  onClick: () => void,
  primary = false
) => (
  <button
    type="button"
    title={title}
    onClick={onClick}
    style={{
      width: primary ? "40px" : "32px",
      height: primary ? "40px" : "32px",
      borderRadius: "50%",
      border: "none",
      background: primary ? "#007aff" : "transparent",
      color: primary ? "#fff" : "#495057",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      cursor: "pointer",
    }}
  >
    {icon}
  </button>
);

const mapState = (state: RootState) => ({
  currentMediaDetailsForEdit: state.media.mediaDetails.media,
});

const connector = connect(mapState);
type Props = ConnectedProps<typeof connector>;
export default connector(MediaChapters);
